export type AysInekInlierType =
  | 'OUTLIER_SHORT_STAY'
  | 'TRANSFER'
  | 'INLIER_PROFITLIER'
  | 'INLIER_COSTLIER'
  | 'OUTLIER_LONG_STAY'
  | 'UNKNOWN'
  /**
   * Superseded by `INLIER_PROFITLIER` + `INLIER_COSTLIER` (BDA-6615). Still
   * shown while saved tiles and stored cases carry it; no longer selectable.
   */
  | 'INLIER';

const INEK_INLIER_TYPES_DISPLAY_NAMES = {
  OUTLIER_SHORT_STAY: 'Kurzlieger',
  TRANSFER: 'Verlegung',
  INLIER_PROFITLIER: 'Profitlier',
  INLIER_COSTLIER: 'Costlier',
  OUTLIER_LONG_STAY: 'Langlieger',
  UNKNOWN: 'Unbekannt',
  INLIER: 'Normallieger',
} as const;

export const AYS_INEK_INLIER_TYPES_DISPLAY_NAMES: {
  [inlierType in AysInekInlierType]: string;
} = INEK_INLIER_TYPES_DISPLAY_NAMES;

export const AYS_INEK_INLIER_TYPE_DESCRIPTIONS: Partial<
  Record<AysInekInlierType, string>
> = {
  TRANSFER: 'Verlegter Fall, für den ein Verlegungsabschlag abgerechnet wird.',
  INLIER_PROFITLIER:
    'Normallieger mit einer Verweildauer unter der mittleren Verweildauer der DRG laut InEK-Katalog.',
  INLIER_COSTLIER:
    'Normallieger mit einer Verweildauer ab der mittleren Verweildauer der DRG laut InEK-Katalog.',
};

/** The inlier types a filter offers, in display order. */
export const AYS_INEK_SELECTABLE_INLIER_TYPES = [
  'OUTLIER_SHORT_STAY',
  'TRANSFER',
  'INLIER_PROFITLIER',
  'INLIER_COSTLIER',
  'OUTLIER_LONG_STAY',
  'UNKNOWN',
] as const satisfies readonly AysInekInlierType[];

// The legacy INLIER sits where its successors are.
const INLIER_TYPE_ORDER: readonly string[] = [
  'OUTLIER_SHORT_STAY',
  'TRANSFER',
  'INLIER',
  'INLIER_PROFITLIER',
  'INLIER_COSTLIER',
  'OUTLIER_LONG_STAY',
  'UNKNOWN',
];

/** Sorts inlier types in display order; values this list does not know go last. */
export function compareInlierTypes(a: string, b: string): number {
  const rank = (type: string) => {
    const index = INLIER_TYPE_ORDER.indexOf(type);
    return index === -1 ? INLIER_TYPE_ORDER.length : index;
  };
  return rank(a) - rank(b);
}

export type InlierTypeDisplayName =
  (typeof INEK_INLIER_TYPES_DISPLAY_NAMES)[keyof typeof INEK_INLIER_TYPES_DISPLAY_NAMES];

export type SelectableInlierTypeDisplayName =
  (typeof INEK_INLIER_TYPES_DISPLAY_NAMES)[(typeof AYS_INEK_SELECTABLE_INLIER_TYPES)[number]];

export function getInlierTypeCodeFromDisplayName(
  displayName: InlierTypeDisplayName,
): AysInekInlierType {
  const entry = Object.entries(INEK_INLIER_TYPES_DISPLAY_NAMES).find(
    ([, name]) => name === displayName,
  );
  if (!entry) {
    throw new Error(`Unknown inlier type display name: ${displayName}`);
  }
  return entry[0] as AysInekInlierType;
}
